/**
 * La grabación, del lado del navegador.
 *
 * ── POR QUÉ EL MICRÓFONO ES EL CAMPO PRIMARIO ──────────────────────────────
 * Un formulario aburre y se contesta con monosílabos; hablar saca historias. Y
 * la calidad de lo que se cuenta NO es cosmética: una hoja de vida armada sobre
 * "trabajé duro 3 años" coloca menos que una armada sobre "manejaba un tractor
 * John Deere en una finca de 200 hectáreas". Por eso el micrófono es el campo, y
 * escribir es la alternativa —visible siempre, nunca la invitación.
 *
 * ── LOS DOS CONTENEDORES QUE EXISTEN EN LA CALLE ────────────────────────────
 * Chrome/Android graba `audio/webm;codecs=opus`. Safari/iOS no sabe hacer webm y
 * graba `audio/mp4`. Los dos los acepta el backend. `MediaRecorder` no negocia:
 * si se le pasa un mimeType que no soporta, LANZA en el constructor — así que
 * acá se pregunta primero con `isTypeSupported` y, si ninguno pasa, se lo deja
 * elegir al navegador (undefined) en vez de imponerle uno.
 *
 * `isTypeSupported` es un método estático que Safari trae desde la 14.1, pero
 * hay WebViews viejos donde `MediaRecorder` existe sin él: por eso la llamada va
 * dentro de un guard y no directo.
 */

/* ── la analítica de colocación ──────────────────────────────────────────────
 * Estas dos llaves viajan con el cuestionario y son la razón de ser de todo
 * esto: sin ellas no se puede responder si quien HABLÓ coloca más que quien
 * escribió, ni si una respuesta `thin` predice una postulación que no prospera.
 * Viven acá y no en el cliente de API porque el borrador las guarda mucho antes
 * de que exista una petición. */

export type AnswerQuality = 'rich' | 'ok' | 'thin';
export type InputMode = 'voice' | 'text';

export const ANSWER_QUALITIES = ['rich', 'ok', 'thin'] as const;
export const INPUT_MODES = ['voice', 'text'] as const;

/** El tope del contrato: más de 90 s el servidor los rechaza con 413. */
export const MAX_RECORDING_SECONDS = 90;

/** Cuándo se avisa que se está acabando el tiempo, sin cortar a nadie. */
export const WARN_RECORDING_SECONDS = 75;

/** El tope de peso del servidor, para no subir 8 MB que ya sabemos que caen. */
export const MAX_AUDIO_BYTES = 8 * 1024 * 1024;

/**
 * Un audio más liviano que esto es el "apretar y soltar" sin querer: el backend
 * lo cuenta como `empty_audio`, así que ni se sube.
 */
export const MIN_AUDIO_BYTES = 1024;

/** En orden de preferencia; el primero que el navegador soporte. */
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/m4a',
  'audio/ogg;codecs=opus',
] as const;

export function isRecordingSupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.MediaRecorder === 'undefined') return false;
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/** El contenedor que este navegador sabe grabar, o `undefined` para dejarlo elegir. */
export function pickRecorderMimeType(): string | undefined {
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return undefined;
  const supports = window.MediaRecorder.isTypeSupported;
  if (typeof supports !== 'function') return undefined;
  return MIME_CANDIDATES.find((type) => {
    try {
      return supports.call(window.MediaRecorder, type);
    } catch {
      return false;
    }
  });
}

/**
 * La extensión que corresponde al contenedor.
 *
 * OpenAI detecta el formato por el NOMBRE del archivo, no por el mimetype del
 * multipart. El servidor ya lo normaliza antes de reenviar, pero mandar
 * `answer.webm` y no `blob` deja el rastro legible en los logs de los dos lados.
 */
export function extensionFor(mimeType: string): string {
  const base = (mimeType || '').split(';')[0].trim().toLowerCase();
  if (base.includes('mp4') || base.includes('m4a')) return 'm4a';
  if (base.includes('ogg')) return 'ogg';
  if (base.includes('mpeg') || base.includes('mp3')) return 'mp3';
  if (base.includes('wav')) return 'wav';
  return 'webm';
}

/** 65 → "1:05". El timer de la grabación se lee de un vistazo, no en segundos. */
export function formatSeconds(total: number): string {
  const safe = Math.max(0, Math.floor(total));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * La respuesta a una repregunta se SUMA a la anterior; no la reemplaza.
 *
 * Es toda la mecánica del coaching: quien contesta "recolectaba café" y después
 * "eran como 200 hectáreas" dijo las dos cosas, y la hoja de vida las necesita
 * juntas. Se pega con un espacio y no con un salto de línea porque el prompt del
 * CV lee esto como una transcripción corrida, no como viñetas.
 */
export function appendAnswer(previous: string, addition: string, limit: number): string {
  const before = previous.trim();
  const next = addition.trim();
  if (!next) return before;
  if (!before) return next.slice(0, limit);
  const joined = /[.!?]$/.test(before) ? `${before} ${next}` : `${before}. ${next}`;
  return joined.slice(0, limit);
}
